import { TeContainerSlim, TeRadio, TeRadioGroup, TeText } from '@topcon/tuicl-react';
import { borderRadiusToPixelString, paddingToPixelString } from '@topcon/tuicl/dist/common/utils';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useState } from 'react';

import {
  AddressInfo,
  RequestBusinessPartnerDeclineReasonEnum,
  ServiceAgreementDeclineReasonEnum,
} from '../../api/generated/pm';
import { border } from '../../providers/theme/theme.index';
import ConfirmDeletionModal from '../Modal/ConfirmDeletionModal';
import useServiceAgreementId from '../../hooks/useServiceAgreementId';
import { useDeclineRequestBusinessPartner } from '../../pages/RequestBusinessPartner/businessPartnerRequest.mutations';

interface DeclineServiceAgreementModalProps {
  type: 'requestBusinessPartner' | 'serviceAgreement';
  open: boolean;
  onClose: (shouldUpdate: boolean) => void;
  onDecline: () => void;
  id: string;
  company: {
    name: string;
    companyAddress: AddressInfo;
  };
}

function DeclineServiceAgreementModal({
  open,
  onDecline,
  onClose,
  id,
  company,
  type,
}: DeclineServiceAgreementModalProps) {
  const { t } = useTranslation();
  const [declineReasonKey, setDeclineReasonKey] = useState<
    ServiceAgreementDeclineReasonEnum | RequestBusinessPartnerDeclineReasonEnum
  >(ServiceAgreementDeclineReasonEnum.REASON_NONE);

  const { declineAgreement, isPendingDeclineServiceAgreement } = useServiceAgreementId(id);
  const { mutateAsync: declineRequest, isPending: isPendingDeclineRequest } =
    useDeclineRequestBusinessPartner();
  const fullAddress = `${company?.companyAddress?.addressLineOne}, ${company?.companyAddress?.zipPostalCode} ${company?.companyAddress?.city}, ${company?.companyAddress?.country}`;

  const handleSubmit = async () => {
    if (type === 'serviceAgreement')
      await declineAgreement(declineReasonKey as ServiceAgreementDeclineReasonEnum);
    else
      await declineRequest({
        id,
        declineReason: declineReasonKey as RequestBusinessPartnerDeclineReasonEnum,
      });

    onDecline();
  };

  return (
    <ConfirmDeletionModal
      title={t('serviceAgreement.decline')}
      description={t('serviceAgreement.declineModal.disclaimer')}
      primaryButtonText={t('serviceAgreement.decline')}
      text={company.name}
      subText={fullAddress}
      secondaryButtonText={t('close')}
      open={open}
      iconName={'business'}
      onSubmit={handleSubmit}
      onClose={onClose}
      loading={isPendingDeclineServiceAgreement || isPendingDeclineRequest}
    >
      <TeContainerSlim fill grow column gap="s">
        <TeText weight={700} size={12}>
          {t('declineReason')}
        </TeText>
        <TeRadioGroupWrapper
          selectedByDefault="two"
          direction="column"
          gap={0}
          techange={(e) =>
            setDeclineReasonKey(
              type === 'serviceAgreement'
                ? (e.value as ServiceAgreementDeclineReasonEnum)
                : (e.value as RequestBusinessPartnerDeclineReasonEnum)
            )
          }
          selected={declineReasonKey}
        >
          {Object.entries(ServiceAgreementDeclineReasonEnum)
            .filter((entry) => isNaN(+entry[1]))
            .map(([key, value]) => (
              <TeRadioWrapper
                value={value}
                label={t(`serviceAgreement.declineModal.reasons.${value}`)}
                key={key}
              />
            ))}
        </TeRadioGroupWrapper>
      </TeContainerSlim>
    </ConfirmDeletionModal>
  );
}

const TeRadioGroupWrapper = styled(TeRadioGroup)`
  width: 100%;
  border-radius: ${borderRadiusToPixelString('m')};
  border: 1px solid ${border.primary};
`;

const TeRadioWrapper = styled(TeRadio)`
  border-top: 1px solid ${border.primary};
  width: 100% !important;
  padding: ${paddingToPixelString('xs')};
  &:first-child {
    border-top: unset;
  }
`;
export default DeclineServiceAgreementModal;
